import { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal, Overline } from "@/components/primitives";
import { scrollToPricing } from "@/lib/scroll";
import { assets } from "@/config/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinalCTA() {
  const [form, setForm] = useState({ name: "", email: "", business: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", business: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-black py-24 text-white lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-grid-dark opacity-50" />
      <img
        src={assets.lifestyle.restaurantOwner}
        alt=""
        className="pointer-events-none absolute bottom-0 right-0 h-full w-1/2 object-cover opacity-[0.12] [mask-image:linear-gradient(to_left,black,transparent)]"
      />

      <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8">
        {/* CTA banner */}
        <div className="max-w-4xl">
          <Reveal><Overline dark>Get started</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.0] tracking-tighter sm:text-5xl lg:text-6xl">
              Ready to turn your reputation into a growth engine?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-white/60">
              Start collecting more reviews, improving visibility, and building trust with
              one intelligent platform.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                data-testid="final-cta-btn"
                onClick={scrollToPricing}
                className="group inline-flex items-center justify-center gap-2 rounded-[10px] bg-white px-7 py-4 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5"
              >
                Get Started
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <span className="text-sm font-light text-white/50">
                Simple setup. Clear results. Built for modern local businesses.
              </span>
            </div>
          </Reveal>
        </div>

        {/* Contact form */}
        <div className="mt-20 grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <Reveal>
            <h3 className="text-2xl font-medium tracking-tight sm:text-3xl">
              Have a question first?
            </h3>
            <p className="mt-4 max-w-md text-base font-light leading-relaxed text-white/55">
              Send us a note and we'll get back to you. Ready to dive in now? You can
              start from any plan above — no sales calls required.
            </p>
            <button
              data-testid="contact-jump-pricing"
              onClick={scrollToPricing}
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/80 underline-offset-4 hover:underline"
            >
              Jump to pricing <ArrowRight size={14} />
            </button>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-[10px] border border-white/12 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-medium text-white/60">Name</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="w-full rounded-[10px] border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/50"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-medium text-white/60">Email</label>
                  <input
                    id="contact-email"
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@business.com"
                    className="w-full rounded-[10px] border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/50"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="contact-business" className="mb-1.5 block text-xs font-medium text-white/60">Business name <span className="text-white/30">(optional)</span></label>
                <input
                  id="contact-business"
                  data-testid="contact-business"
                  value={form.business}
                  onChange={update("business")}
                  placeholder="e.g. Bloom Café"
                  className="w-full rounded-[10px] border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/50"
                />
              </div>
              <div className="mt-4">
                <label htmlFor="contact-message" className="mb-1.5 block text-xs font-medium text-white/60">Message</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Tell us a little about your business…"
                  className="w-full resize-none rounded-[10px] border border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-white/50"
                />
              </div>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-white py-3.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
